import { Component, OnInit } from '@angular/core';
import { MainLayoutComponent } from '../../_layouts/main-layout/main-layout.component';

import { FormGroup, FormControl, ReactiveFormsModule, Validators, AbstractControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { UserService } from '../../_services/users.service';
import { AlertService } from '../../_services/alert.service';
import { userRoles } from '../../../Shared/_constants/constants';

// Self-service role editing is restricted to this one account - everyone
// else's role is managed by an admin via Admin Users, not from their own
// profile page.

@Component({
    selector: 'app-profile',
    imports: [MainLayoutComponent, ReactiveFormsModule, MatButtonModule, MatIconModule],
    templateUrl: './profile.component.html',
    styleUrl: './profile.component.css'
})
export class ProfileComponent implements OnInit {
  loading = true;
  savingProfile = false;
  savingPassword = false;
  useremail = '';
  userrole = '';
  canEditRole = false;
  readonly allRoles = Object.values(userRoles);
  selectedRoles: string[] = [];

  profileForm: FormGroup = new FormGroup({
    firstname: new FormControl('', Validators.required),
    lastname: new FormControl('', Validators.required),
    displayname: new FormControl(''),
    phone: new FormControl(''),
    vatnumber: new FormControl(''),
  });

  passwordForm: FormGroup = new FormGroup({
    oldPwd: new FormControl('', Validators.required),
    newPwd: new FormControl('', [Validators.required, Validators.minLength(8)]),
    confirmPwd: new FormControl('', Validators.required),
  });

  constructor(private usersService: UserService, private alertService: AlertService) {}

  get p(): { [key: string]: AbstractControl } {
    return this.profileForm.controls;
  }

  get pwd(): { [key: string]: AbstractControl } {
    return this.passwordForm.controls;
  }

  ngOnInit(): void {
    this.usersService.getUser().subscribe({
      next: (user) => {
        this.useremail = user?.useremail || '';
        this.userrole = user?.userrole || '';
        this.canEditRole = this.usersService.isSuperAdmin();
        this.selectedRoles = this.userrole ? this.userrole.split(',').map((r: string) => r.trim()).filter(Boolean) : [];
        this.profileForm.patchValue({
          firstname: user?.firstname || '',
          lastname: user?.lastname || '',
          displayname: user?.displayname || '',
          phone: user?.phone || '',
          vatnumber: user?.vatnumber || '',
        });
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.alertService.alert(error?.error?.message || 'Could not load your profile', 'error');
      },
    });
  }

  isRoleSelected(role: string): boolean {
    return this.selectedRoles.includes(role);
  }

  toggleRole(role: string, checked: boolean): void {
    this.selectedRoles = checked
      ? [...this.selectedRoles, role]
      : this.selectedRoles.filter((r) => r !== role);
  }

  onSaveProfile(): void {
    if (this.profileForm.invalid || this.savingProfile) {
      this.profileForm.markAllAsTouched();
      return;
    }
    if (this.canEditRole && this.selectedRoles.length === 0) {
      this.alertService.alert('Select at least one role', 'error');
      return;
    }
    this.savingProfile = true;
    const payload: any = { ...this.profileForm.value };
    // Only S-ADMIN accounts can change their own role.
    if (this.canEditRole) {
      payload.userrole = this.selectedRoles.join(',');
    }
    // No id passed - the backend updates the logged-in user's own record.
    this.usersService.updateUser(payload).subscribe({
      next: () => {
        this.savingProfile = false;
        this.alertService.alert('Profile updated successfully', 'success');
      },
      error: (error) => {
        this.savingProfile = false;
        this.alertService.alert(error?.error?.message || 'Could not update your profile', 'error');
      },
    });
  }

  onChangePassword(): void {
    if (this.passwordForm.invalid || this.savingPassword) {
      this.passwordForm.markAllAsTouched();
      return;
    }
    const { oldPwd, newPwd, confirmPwd } = this.passwordForm.value;
    if (newPwd !== confirmPwd) {
      this.alertService.alert('New password and confirmation do not match', 'error');
      return;
    }
    this.savingPassword = true;
    this.usersService.updatePwd(oldPwd, newPwd).subscribe({
      next: () => {
        this.savingPassword = false;
        this.passwordForm.reset();
        this.alertService.alert('Password changed successfully', 'success');
      },
      error: (error) => {
        this.savingPassword = false;
        this.alertService.alert(error?.error?.message || 'Could not change your password', 'error');
      },
    });
  }
}
